import type { AiAction, QuotaMetadata } from "./types";

export type AiActionCost = {
  action: AiAction;
  requiredUnits: number;
  costVersion: string;
  labelKey: string;
};

export type AiActionCostConfig = Partial<Record<AiAction, number>>;

const PHASE4_COST_VERSION = "phase4.quota-costs.v1";
const PHASE5_COST_VERSION = "phase5.quota-costs.v1";
const PHASE6_COST_VERSION = "phase6.quota-costs.v1";

export const defaultAiActionCosts: Record<AiAction, AiActionCost> = {
  "phase4.pdf_parse.v1": {
    action: "phase4.pdf_parse.v1",
    requiredUnits: 2,
    costVersion: PHASE4_COST_VERSION,
    labelKey: "pdf_parse",
  },
  "phase4.master_plan.v1": {
    action: "phase4.master_plan.v1",
    requiredUnits: 3,
    costVersion: PHASE4_COST_VERSION,
    labelKey: "master_plan",
  },
  "phase4.target_plan.v1": {
    action: "phase4.target_plan.v1",
    requiredUnits: 4,
    costVersion: PHASE4_COST_VERSION,
    labelKey: "target_plan",
  },
  "phase4.item_edit.v1": {
    action: "phase4.item_edit.v1",
    requiredUnits: 1,
    costVersion: PHASE4_COST_VERSION,
    labelKey: "item_edit",
  },
  "phase4.master_pdf_merge.v1": {
    action: "phase4.master_pdf_merge.v1",
    requiredUnits: 3,
    costVersion: PHASE4_COST_VERSION,
    labelKey: "master_pdf_merge",
  },
  "phase5.jd_parse.v1": {
    action: "phase5.jd_parse.v1",
    requiredUnits: 1,
    costVersion: PHASE5_COST_VERSION,
    labelKey: "jd_parse",
  },
  "phase5.first_target_from_raw_master.v1": {
    action: "phase5.first_target_from_raw_master.v1",
    requiredUnits: 4,
    costVersion: PHASE5_COST_VERSION,
    labelKey: "first_target_from_raw_master",
  },
  "phase5.master_review.v1": {
    action: "phase5.master_review.v1",
    requiredUnits: 4,
    costVersion: PHASE5_COST_VERSION,
    labelKey: "master_review",
  },
  "phase5.target_review.v1": {
    action: "phase5.target_review.v1",
    requiredUnits: 4,
    costVersion: PHASE5_COST_VERSION,
    labelKey: "target_review",
  },
  "phase5.review_master_updates.v1": {
    action: "phase5.review_master_updates.v1",
    requiredUnits: 3,
    costVersion: PHASE5_COST_VERSION,
    labelKey: "review_master_updates",
  },
  "phase5.review_jd_changes.v1": {
    action: "phase5.review_jd_changes.v1",
    requiredUnits: 3,
    costVersion: PHASE5_COST_VERSION,
    labelKey: "review_jd_changes",
  },
  "phase5.prompt_review.v1": {
    action: "phase5.prompt_review.v1",
    requiredUnits: 4,
    costVersion: PHASE5_COST_VERSION,
    labelKey: "prompt_review",
  },
  "phase5.section_regenerate.v1": {
    action: "phase5.section_regenerate.v1",
    requiredUnits: 2,
    costVersion: PHASE5_COST_VERSION,
    labelKey: "section_regenerate",
  },
  "phase5.item_regenerate.v1": {
    action: "phase5.item_regenerate.v1",
    requiredUnits: 2,
    costVersion: PHASE5_COST_VERSION,
    labelKey: "item_regenerate",
  },
  "phase6.jd_analysis.v1": {
    action: "phase6.jd_analysis.v1",
    requiredUnits: 1,
    costVersion: PHASE6_COST_VERSION,
    labelKey: "jd_analysis",
  },
  "phase6.jd_proposal_generation.v1": {
    action: "phase6.jd_proposal_generation.v1",
    requiredUnits: 4,
    costVersion: PHASE6_COST_VERSION,
    labelKey: "jd_proposal_generation",
  },
  "phase6.master_pickup_review.v1": {
    action: "phase6.master_pickup_review.v1",
    requiredUnits: 3,
    costVersion: PHASE6_COST_VERSION,
    labelKey: "master_pickup_review",
  },
};

export function getAiActionCost({
  action,
  config = {},
}: {
  action: AiAction;
  metadata?: QuotaMetadata;
  config?: AiActionCostConfig;
}): AiActionCost {
  const defaultCost = defaultAiActionCosts[action];
  const configuredUnits = config[action];

  return {
    ...defaultCost,
    requiredUnits: configuredUnits ?? defaultCost.requiredUnits,
  };
}
